import React from "react";
import styled from "styled-components";
import PropTypes from "prop-types";

import CustomDropdown from "core/CustomDropdown";
import {
    useCustomDropdown,
    CustomDropdownProvider
} from "core/CustomDropdown/hooks/useCustomDropdown";

import Button from "core/Button";
import colors from "core/colors";
import { CancelButton } from "./styles";

import {
    reusableComponentSizes,
    buttonStyleTypes,
    positionalAlignments,
    justifyContentPosition
} from "core/props";

const UnstyledActionWarning = ({
    className,
    heading,
    onSubmit,
    onCancel,
    submitText,
    cancelText,
    width,
    triggerText,
    isLinkTriggerStyle,
    buttonProps,
    showBoxShadow,
    bgColor,
    headingColor,
    footerChildren,
    showCloseButton
}) => {
    const customDropdownProps = useCustomDropdown({
        triggerText: triggerText,
        isLinkTriggerStyle: isLinkTriggerStyle,
        buttonProps: buttonProps,
        showBoxShadow: showBoxShadow,
        bgColor: bgColor,
        headingColor: headingColor,
        footerChildren: footerChildren,
        width: width,
        heading: heading,
        showCloseButton: showCloseButton
    });

    const { handleClose } = customDropdownProps;

    const handleConfirm = e => {
        e.preventDefault();
        onSubmit(e);
        handleClose();
    };

    const handleCancel = e => {
        e.preventDefault();
        onCancel(e);
        handleClose();
    };

    const Content = () => {
        return (
            <span className={className}>
                <Button size="md" darkMode onClick={e => handleConfirm(e)}>
                    {submitText}
                </Button>

                <CancelButton onClick={e => handleCancel(e)}>
                    {cancelText}
                </CancelButton>
            </span>
        );
    };

    return (
        <CustomDropdown {...customDropdownProps}>
            <Content />
        </CustomDropdown>
    );
};

const ActionWarningComponent = styled(UnstyledActionWarning)``;

const ActionWarning = ({
    className,
    heading,
    onSubmit,
    onCancel,
    submitText,
    cancelText = "Cancel",
    width = "auto",
    triggerText,
    bgColor = colors.functional.lightMode.laser,
    headingColor = colors.primary.diamond,
    isLinkTriggerStyle,
    buttonProps = {
        styleType: "outline",
        size: "lg",
        justifyContent: "flex-start",
        processingIconPosition: "left"
    },
    showBoxShadow = false,
    footerChildren,
    showCloseButton = false
}) => {
    return (
        <CustomDropdownProvider>
            <ActionWarningComponent
                className={className}
                heading={heading}
                onSubmit={onSubmit}
                onCancel={onCancel}
                submitText={submitText}
                cancelText={cancelText}
                width={width}
                triggerText={triggerText}
                isLinkTriggerStyle={isLinkTriggerStyle}
                buttonProps={buttonProps}
                showBoxShadow={showBoxShadow}
                showCloseButton={showCloseButton}
                bgColor={bgColor}
                headingColor={headingColor}
                footerChildren={footerChildren}
            />
        </CustomDropdownProvider>
    );
};

ActionWarning.propTypes = {
    triggerText: PropTypes.oneOfType([PropTypes.element, PropTypes.string])
        .isRequired,
    onSubmit: PropTypes.func.isRequired,
    submitText: PropTypes.string.isRequired,
    onCancel: PropTypes.func,
    cancelText: PropTypes.string,
    heading: PropTypes.string,
    width: PropTypes.string,
    isLinkTriggerStyle: PropTypes.bool,
    buttonProps: PropTypes.shape({
        size: PropTypes.oneOf(reusableComponentSizes),
        styleType: PropTypes.oneOf(buttonStyleTypes),
        width: PropTypes.string,
        darkMode: PropTypes.bool,
        isDisabled: PropTypes.bool,
        isProcessing: PropTypes.bool,
        iconPosition: PropTypes.oneOf(positionalAlignments),
        processingIconPosition: PropTypes.oneOf(positionalAlignments),
        justifyContent: PropTypes.oneOf(justifyContentPosition),
        isPill: PropTypes.bool,
        isActive: PropTypes.bool,
        isToggled: PropTypes.bool,
        isSubmit: PropTypes.bool
    }),
    defaultButtonSelected: PropTypes.string,
    showCloseButton: PropTypes.bool,
    showBoxShadow: PropTypes.bool,
    bgColor: PropTypes.string,
    headingColor: PropTypes.string,
    footerChildren: PropTypes.any,
    handleSubmit: PropTypes.func
};

export default ActionWarning;
